import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Field, Select } from "@/components/common";
import { LANGS, TONES } from "@/pages/Composer";
import { SenderSetupCard } from "@/components/SenderSetup";

const PURPOSES = ["exhibition follow-up", "introduction", "catalogue request", "quotation request", "sample request", "distribution enquiry", "partnership enquiry"];

export default function CampaignNew() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const ids = (params.get("ids") || "").split(",").filter(Boolean);
  const [senders, setSenders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [o, setO] = useState({ name: "", mode: "template", purpose: "exhibition follow-up", language: "English", tone: "professional", length: "medium", notes: "" });
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [later, setLater] = useState(false);
  useEffect(() => {
    api.get("/senders").then((r) => {
      setSenders(r.data);
      setLoaded(true);
      const act = r.data.filter((s) => s.connection_status === "active");
      const pick = r.data.find((s) => s.is_default)?.id || (act.length === 1 ? act[0].id : "");
      if (pick) setO((p) => ({ ...p, sender_id: pick }));
    });
    api.get("/templates").then((r) => setTemplates(r.data.items));
  }, []);
  const set = (k, v) => setO({ ...o, [k]: v });
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/campaigns", { ...o, contact_ids: ids, template_id: o.template_id || null });
      nav(`/campaigns/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="max-w-2xl">
      <PageHeader title="New email batch" subtitle={`${ids.length} contact(s) selected. Next you'll review every message before anything is sent.`} />
      {loaded && senders.length === 0 && !later && <div className="mb-4"><SenderSetupCard testid="campaign-sender-setup" onCreated={(s) => { setSenders([s]); setO((p) => ({ ...p, sender_id: s.id })); }} onLater={() => setLater(true)} /></div>}
      <div className="cd-card space-y-4">
        <Field label="Campaign name (optional)"><input data-testid="campaign-name-input" className="cd-input" value={o.name} onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label="Sender business"><Select testid="campaign-sender-select" value={o.sender_id} onChange={(v) => set("sender_id", v)} placeholder="Choose…" options={senders.map((s) => [s.id, `${s.business_name}${s.connection_status !== "active" ? " (not connected)" : ""}`])} /></Field>
        <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
          {[["template", "Template only", FileText], ["ai", "AI personalised", Sparkles]].map(([m, l, I]) => (
            <button key={m} data-testid={`campaign-mode-${m}`} onClick={() => set("mode", m)} className={`flex h-11 items-center justify-center gap-2 rounded-lg text-sm font-semibold ${o.mode === m ? "bg-white text-teal-700 shadow-sm" : "text-slate-600"}`}><I className="h-4 w-4" />{l}</button>
          ))}
        </div>
        <p className="text-xs text-slate-500">{o.mode === "template" ? "Variables are substituted; the wording is not rewritten." : "A separate AI draft is created for each recipient, using the template (optional) as guidance."}</p>
        <Field label={o.mode === "template" ? "Template" : "Template as guidance (optional)"}><Select testid="campaign-template-select" value={o.template_id} onChange={(v) => set("template_id", v)} placeholder={o.mode === "template" ? "Choose…" : "None"} options={templates.map((t) => [t.id, t.name])} /></Field>
        {o.mode === "ai" && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Purpose"><Select testid="campaign-purpose-select" value={o.purpose} onChange={(v) => set("purpose", v)} options={PURPOSES} /></Field>
            <Field label="Language"><Select testid="campaign-language-select" value={o.language} onChange={(v) => set("language", v)} options={LANGS} /></Field>
            <Field label="Tone"><Select value={o.tone} onChange={(v) => set("tone", v)} options={TONES} /></Field>
            <Field label="Length"><Select value={o.length} onChange={(v) => set("length", v)} options={[["short", "Short"], ["medium", "Medium"], ["long", "Long"]]} /></Field>
            <div className="sm:col-span-2"><Field label="Shared notes (applied to every recipient)"><textarea data-testid="campaign-notes-textarea" rows={3} className="cd-textarea" value={o.notes} onChange={(e) => set("notes", e.target.value)} /></Field></div>
          </div>
        )}
        <button data-testid="campaign-generate-btn" disabled={busy || !o.sender_id || !ids.length || (o.mode === "template" && !o.template_id)} onClick={create} className="cd-btn-primary w-full">{busy ? "Creating…" : "Generate previews"}</button>
      </div>
    </div>
  );
}
